import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { SiteCopy } from "@tih/shared";
import { SITE_COPY_CONTENT_KEY, SITE_COPY_DEFAULT } from "@tih/shared";
import {
  adminFetchContent,
  adminPatchContent,
  fetchSiteCopy,
  ApiError,
} from "../api/client";
import { setAtPath } from "../lib/siteCopyPaths";

type SiteCopyContextValue = {
  copy: SiteCopy;
  loading: boolean;
  refresh: () => Promise<void>;
  /** Merge a string leaf into the `site_copy` patch and persist (admin only). */
  savePath: (path: string, value: string) => Promise<void>;
};

const SiteCopyContext = createContext<SiteCopyContextValue | undefined>(undefined);

export function SiteCopyProvider({ children }: { children: ReactNode }) {
  const [copy, setCopy] = useState<SiteCopy>(SITE_COPY_DEFAULT);
  const [loading, setLoading] = useState(true);
  const patchRef = useRef<Record<string, unknown> | null>(null);
  const patchLoadedRef = useRef(false);
  const saveQueueRef = useRef(Promise.resolve());

  const refresh = useCallback(async () => {
    try {
      const data = await fetchSiteCopy();
      setCopy(data);
    } catch {
      setCopy(SITE_COPY_DEFAULT);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const ensurePatchLoaded = useCallback(async () => {
    if (patchLoadedRef.current) return;
    try {
      const r = await adminFetchContent(SITE_COPY_CONTENT_KEY);
      patchRef.current = structuredClone(r.valueJson as Record<string, unknown>);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        patchRef.current = {};
      } else {
        throw e;
      }
    }
    patchLoadedRef.current = true;
  }, []);

  const savePath = useCallback(
    (path: string, value: string) => {
      const task = saveQueueRef.current.then(async () => {
        await ensurePatchLoaded();
        const delta: Record<string, unknown> = {};
        setAtPath(delta, path, value);
        const saved = await adminPatchContent(SITE_COPY_CONTENT_KEY, delta);
        patchRef.current = structuredClone(saved.valueJson as Record<string, unknown>);
        await refresh();
      });
      saveQueueRef.current = task.catch(() => {});
      return task;
    },
    [ensurePatchLoaded, refresh],
  );

  const value = useMemo<SiteCopyContextValue>(
    () => ({ copy, loading, refresh, savePath }),
    [copy, loading, refresh, savePath],
  );

  return <SiteCopyContext.Provider value={value}>{children}</SiteCopyContext.Provider>;
}

export function useSiteCopy(): SiteCopyContextValue {
  const ctx = useContext(SiteCopyContext);
  if (!ctx) throw new Error("useSiteCopy must be used inside <SiteCopyProvider>");
  return ctx;
}
